/**
 * Wrapper for Angular Http module to handle Infor SSO response formats.
 * The service will redirect the user to login page if the server responds with one of the following status :
 * - TIMEDOUTLOGIN - The user session has timed out and requires a relogin to continue with server interaction
 * - LOGIN - The user is not logged in.
 * - LOGOUTSUCCESSFUL - The user has logged out from the current session. Any further action requires a relogin
 * - LOGINFAILED - Failed to authenticate user for various reasons : invalid credentials, server not responding etc...
 * - REDIRECTING - The user will be redirected to another authenticating service for performing authentication
 *
 * The security server might also respond with request to login explicity using the following assertions
 * - TIMEOUTASSERT - The user session has timed out, need to re-authenticate the user
 * - LOGINASSERT - The user is not logged in
 * - LOGINREQD - The current action requires the user to authenticate
 */
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest,
  HttpResponse, HttpResponseBase
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';

export abstract class SSORequest {
  public static ACTION = {
    TIMEOUTASSERT: 'TIMEOUTASSERT',
    LOGINASSERT: 'LOGINASSERT',
    REGCOMPLETE: 'REGCOMPLETE',
    LOGINREQUIRED: 'LOGINREQD'
  };
  public static STATUS = {
    TIMEDOUTLOGIN: 'TIMEDOUTLOGIN',
    LOGIN: 'LOGIN',
    LOGOUTSUCCESSFUL: 'LOGOUTSUCCESSFUL',
    LOGINFAILED: 'LOGINFAILED',
    REDIRECTING: 'REDIRECTING'
  };
  public static HEADER = {
    REDIRECTURL: 'SSO_REDIRECTURL',
    STATUS: 'SSO_STATUS',
    CLIENTTYPE: '_ssoClientType',
    INVALIDREQUEST: 'INVALIDREQUEST',
    UNAUTHORIZEDACCESS: 'UNAUTHORIZEDACCESS',
    ADMINMESSAGE: 'ADMINMESSAGE'
  };
  public static QUERYPARAMETER = {
    ORIGINAL_URL: '_ssoOrigUrl',
    ACTION: '_action'
  };
}

@Injectable()
export class HttpSSOService implements HttpInterceptor {
  private static MODULE_NAME = 'HttpSSOService';
  private static PATH_SSOSERVLET = '/SSOServlet?';
  private static PATH_SSO_HTML = '/sso.html';
  private static MIME_TEXT_XML = 'text/xml';

  constructor() {
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const me = this;
    let headers: HttpHeaders = null;
    if (request instanceof HttpRequest) {
      headers = request.headers;
      // me.debugLog('Request URL ${request.url}', 'request');
    } else {
      headers = new HttpHeaders();
      // me.debugLog('Request URL  ${request}', 'request');
    }

    request = request.clone({
      setHeaders: {
        _ssoClientType: 'MSXML'
      }
    });
    return next.handle(request).pipe(tap((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse) {
        this.validateResponse(event);
      }
    }, (err: any) => {
      if (err instanceof HttpErrorResponse) {
        this.handleXHRErrors(err);
      }
    }));
  }

  private validateResponse = (response: HttpResponseBase) => {
    const me = this;
    if (response) {
      me.validateRequest(response);

      if (me.loginRequired(response)) {
        me.doLogin(response);
      }
    }
  }

  private handleXHRErrors = (response: HttpErrorResponse) => {
    const me = this;
    // give the HTTP Request a chance to perform SSO validation and redirect to login page if required
    if (response.error.error instanceof SyntaxError) {
      me.validateResponse(response);
    }
  }

  private loginRequired(response: HttpResponseBase) {
    const me = this;
    let isRequired = false;
    let httpHeaders: HttpHeaders;

    if (response != null) {
      httpHeaders = response.headers;
      if (httpHeaders != null) {
        let ssoStatus = httpHeaders.get(SSORequest.HEADER.STATUS);

        if (ssoStatus !== null) {
          ssoStatus = ssoStatus.toUpperCase();
          switch (ssoStatus) {
            case SSORequest.STATUS.TIMEDOUTLOGIN:
            case SSORequest.STATUS.LOGIN:
            case SSORequest.STATUS.LOGOUTSUCCESSFUL:
            case SSORequest.STATUS.LOGINFAILED:
              isRequired = true;
              break;
            case SSORequest.STATUS.REDIRECTING: {
              isRequired = this.loginRequiredOnRedirect(response);
            }
          }
        } else {
          // check the response for SSO Xml
          isRequired = this.parseSSOResponse(response);
        }
      }
    }
    if (isRequired) {
      me.debugLog('Session is not valid or expired. Requires user to login.', 'loginRequired');
    }
    return isRequired;
  }

  // verify if the server redirect request is sent by the security server
  private loginRequiredOnRedirect(response: HttpResponseBase): boolean {
    const me = this;
    let isRequired = false;
    const httpHeaders: HttpHeaders = response.headers;

    if (httpHeaders != null) {
      const redirectURL = httpHeaders.get(SSORequest.HEADER.REDIRECTURL);
      if (redirectURL) {
        if (redirectURL.indexOf(HttpSSOService.PATH_SSO_HTML) > 0) {
          isRequired = false;
        } else if (redirectURL.indexOf(HttpSSOService.PATH_SSOSERVLET) > 0) {
          const actionValue = this.getUrlParameterValue(redirectURL, SSORequest.QUERYPARAMETER.ACTION);
          switch (actionValue) {
            case SSORequest.ACTION.TIMEOUTASSERT:
            case SSORequest.ACTION.LOGINASSERT:
            case SSORequest.ACTION.REGCOMPLETE:
            case SSORequest.ACTION.LOGINREQUIRED: {
              isRequired = true;
              break;
            }
          }
        }
      }

      if (isRequired) {
        me.debugLog('Session is not valid or expired. User will be redirected to login page.', 'loginRequiredOnRedirect');
      }
      return isRequired;
    }
  }

  // Check if the request sent to the server was valid
  // if the request was invalid, the server will add appropriate headers to signify the same
  private validateRequest(response: HttpResponseBase): boolean {
    const me = this;
    const isValid = true;
    const httpHeaders: HttpHeaders = response.headers;

    if (httpHeaders.has(SSORequest.HEADER.INVALIDREQUEST) || httpHeaders.has(SSORequest.HEADER.UNAUTHORIZEDACCESS)) {
      me.debugLog('The AJAX request is invalid.', 'validateRequest');

      if (httpHeaders.has(SSORequest.HEADER.INVALIDREQUEST)) {
        me.debugLog('The AJAX request is invalid.', 'validateRequest');
        throwError(new Error('Invalid HTTP Request'));
      } else if (httpHeaders.has(SSORequest.HEADER.UNAUTHORIZEDACCESS)) {
        me.debugLog('The user is unauthorized to perform the AJAX request.', 'validateRequest');
        throwError(new Error('Unauthorized HTTP Request'));
      }

      return isValid;
    }
  }

  // parse the sso xml document returned by the server to verify if we need to redirect
  private parseSSOResponse(response: HttpResponseBase): boolean {
    const me = this;
    let isRequired = false;

    const xPathSSOStatus = '//SSO/SSO_STATUS/text()';
    const ssoStatus = me.getNodeValue(response, xPathSSOStatus);
    isRequired = (ssoStatus && ssoStatus.toUpperCase() === SSORequest.STATUS.REDIRECTING) ? true : false;
    return isRequired;
  }

  private doLogin(response: HttpResponseBase) {
    const me = this;
    try {
      me.debugLog('Redirecting user to login page.', 'doLogin');

      // get the current page url to redirect to when the user login completes successfully
      const loc = window.location;
      // ssoOrigUrl = encodeURIComponent(ssoOrigUrl);

      let ssoOrigUrl = loc.href;
      ssoOrigUrl = encodeURIComponent(ssoOrigUrl);

      me.debugLog('SSO Original URL where the user would be redirected after successful login : ' + ssoOrigUrl, 'doLogin');
      // Find the url that should be used for modal window
      // let redirectURL = httpHeaders.get(SSORequest.HEADER.REDIRECTURL);
      const redirectURL = me.getRedirectURL(response);

      // Update the redirect url ssoOrigUrl parameter to redirect to the current root page instead of the ajax request url
      const src = this.updateQueryString(redirectURL, SSORequest.QUERYPARAMETER.ORIGINAL_URL, ssoOrigUrl);
      // open the login page in the parent window itself; do not use iframe to thwart click jacking offence
      //      loc.assign(src);
      me.assign(src);
    } catch (e) {
      /*console.log('HTTPSSOService.doLogin: error occured during ajax request handling');
       console.log('[Error] : ' + e);
       console.log(e);*/

      me.debugLog('An error occured while trying to redirect user to login page', 'doLogin');
      me.debugLog('[Error] : ' + e, 'doLogin');
    }
  }

  private getUrlParameterValue(url: string, queryParamName: string) {
    const me = this;
    const queryParams: HttpParams = me.getURLQueryParams(url);
    return queryParams.get(queryParamName);
  }

  // Add or set a parameter with name 'paramName' to the value 'paramValue' in the returned url string
  private updateQueryString(url: string, paramName: string, paramValue: string) {
    const me = this;
    const queryParams: HttpParams = me.getURLQueryParams(url);
    let hostUrl: string = url;

    if (url.indexOf('?') > 0) {
      hostUrl = url.substring(0, url.indexOf('?'));
    }

    // need to contstruct a new HttpParams object as it is immutable
    const httpParamOptions: {[param: string]: string} = {};
    queryParams.keys().forEach(key => {
      if (key === paramName) {
        httpParamOptions[paramName] = paramValue;
      } else {
        httpParamOptions[key] = queryParams.get(key);
      }
    });
    const httpParams = new HttpParams({fromObject: httpParamOptions});
    return hostUrl + '?' + this.combineQueryParams(httpParams);
  }

  private getURLQueryParams(url: string): HttpParams {
    let urlSearchParams: HttpParams = new HttpParams();
    let queryString: string;

    if (url.indexOf('?') > 0) {
      queryString = url.substring(url.indexOf('?') + 1);
      urlSearchParams = new HttpParams({fromString: queryString});
    }
    return urlSearchParams;
  }

  private combineQueryParams(queryParams: HttpParams) {
    const paramsList = [];

    for (const key of queryParams.keys()) {
      paramsList.push(key + '=' + queryParams.get(key));
    }

    return paramsList.join('&');
  };

  private getRedirectURL(response: HttpResponseBase): string {
    const me = this;
    let redirectUrl = '';
    const httpHeaders: HttpHeaders = response.headers;

    if (httpHeaders.has(SSORequest.HEADER.REDIRECTURL)) {
      redirectUrl = httpHeaders.get(SSORequest.HEADER.REDIRECTURL);
    } else {
      const xPathSSORedirectUrl = '//SSO/SSO_REDIRECTURL/text()';
      redirectUrl = me.getNodeValue(response, xPathSSORedirectUrl);
    }

    if (!redirectUrl) {
      // unable to redirect user to a valid login page
      me.debugLog('Redirect URL for login is not specified in the HTTP response.', 'getRedirectURL');
      throwError(new Error('Redirect URL for login is not specified in the HTTP response.'));
    }
    return redirectUrl;
  }

  private getXmlDoc(response: HttpResponseBase): Document {
    const me = this;
    let xmlDoc: Document = null;

    const responseBody: string = response.toString();
    if (responseBody != null && responseBody.length > 0) {
      try {
        const domParser: DOMParser = new DOMParser();

        // ng7, ts3.1.6 ng build complaining about arg type mismatch?!?
        xmlDoc = domParser.parseFromString(responseBody, 'text/xml'); // HttpSSOService.MIME_TEXT_XML
      } catch (e) {
        me.debugLog(e, 'getXmlDoc');
      }
    }
    return xmlDoc;
  }

  private getNodeValue(response: HttpResponseBase, nodeXPath: string): string {
    const me = this;
    let nodeValue = '';

    const xmlDoc: Document = me.getXmlDoc(response);
    if (xmlDoc) {
      try {
        const xmlNode = xmlDoc.evaluate(nodeXPath, xmlDoc, null, XPathResult.STRING_TYPE, null);
        if (xmlNode) {
          nodeValue = xmlNode.stringValue;
        }
      } catch (e) {
        me.debugLog(e, 'getNodeValue');
      }
    }
    return nodeValue;
  }

  // debug log helper function,
  private debugLog(message: string, functionName?: string) {
    console.log('[${' + HttpSSOService.MODULE_NAME + '} - ${' + functionName + '}] ${' + message + '}');
  }

  private assign(src: string) {
    window.location.assign(src);
  }
}
